import { BlockDefinition, PlaceholderGroup } from './types';

/** Quotation/invoice-specific blocks for the drag-and-drop document builder.
 * Every {{placeholder}} used here matches QuotationPdfService.buildContext's
 * data context exactly (sendifier_api/src/modules/quotations/services/
 * quotation-pdf.service.ts) — adding a new block here that references a
 * placeholder not present there would silently render blank. */
export const QUOTATION_BLOCKS: BlockDefinition[] = [
  {
    type: 'logo',
    label: 'Logo',
    icon: 'image-outline',
    defaultWidth: 160,
    defaultHeight: 60,
    html: `<img src="{{business.logoUrl}}" style="max-width:100%;max-height:100%;object-fit:contain;" />`,
  },
  {
    type: 'business-info',
    label: 'Business Info',
    icon: 'business-outline',
    defaultWidth: 260,
    defaultHeight: 80,
    editableText: true,
    html: `<div style="font-size:13px;line-height:1.5;color:#111827;"><strong>{{business.name}}</strong><br/>{{business.address}}<br/>{{business.phone}} · {{business.email}}</div>`,
  },
  {
    type: 'document-title',
    label: 'Document Title',
    icon: 'document-text-outline',
    defaultWidth: 280,
    defaultHeight: 60,
    editableText: true,
    html: `<div style="font-size:26px;font-weight:700;color:{{colors.primary}};">{{documentTitle}} #{{documentNumber}}</div><div style="font-size:12px;color:{{colors.secondary}};margin-top:4px;">{{date}}</div>`,
  },
  {
    type: 'customer-info',
    label: 'Customer Info',
    icon: 'person-outline',
    defaultWidth: 260,
    defaultHeight: 100,
    editableText: true,
    html: `<div style="font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:{{colors.secondary}};margin-bottom:6px;">Bill To</div><div style="font-size:13px;line-height:1.5;color:#111827;">{{customerName}}<br/>{{customerPhone}}<br/>{{customerEmail}}<br/>{{customerAddress}}</div>`,
  },
  {
    type: 'items-table',
    label: 'Items Table',
    icon: 'list-outline',
    defaultWidth: 560,
    defaultHeight: 220,
    html: `<table style="width:100%;height:100%;border-collapse:collapse;font-size:13px;">
<thead><tr>
<th style="background:{{colors.primary}};color:#fff;text-align:left;padding:8px 10px;">Item</th>
<th style="background:{{colors.primary}};color:#fff;text-align:left;padding:8px 10px;">Qty</th>
<th style="background:{{colors.primary}};color:#fff;text-align:left;padding:8px 10px;">Unit Price</th>
<th style="background:{{colors.primary}};color:#fff;text-align:left;padding:8px 10px;">Total</th>
</tr></thead>
<tbody>
<!--{{#each items}}-->
<tr><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;">{{this.name}}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;">{{this.quantity}}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;">{{../currency}} {{this.unitPrice}}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;">{{../currency}} {{this.totalPrice}}</td></tr>
<!--{{/each}}-->
</tbody>
</table>`,
  },
  {
    type: 'totals',
    label: 'Totals',
    icon: 'calculator-outline',
    defaultWidth: 260,
    defaultHeight: 140,
    html: `<div style="font-size:13px;">
<div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Subtotal</span><span>{{currency}} {{subtotal}}</span></div>
<div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Tax</span><span>{{currency}} {{tax}}</span></div>
<div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Shipping</span><span>{{currency}} {{shipping}}</span></div>
<div style="display:flex;justify-content:space-between;padding:4px 0;"><span>Discount</span><span>-{{currency}} {{discount}}</span></div>
<div style="display:flex;justify-content:space-between;padding:8px 0;margin-top:4px;border-top:2px solid {{colors.primary}};font-weight:700;color:{{colors.primary}};font-size:16px;"><span>Total</span><span>{{currency}} {{totalPrice}}</span></div>
</div>`,
  },
  {
    type: 'notes',
    label: 'Notes',
    icon: 'chatbox-ellipses-outline',
    defaultWidth: 400,
    defaultHeight: 60,
    editableText: true,
    html: `<div style="font-size:12px;color:{{colors.secondary}};"><strong>Notes:</strong> {{notes}}</div>`,
  },
  {
    type: 'text',
    label: 'Custom Text',
    icon: 'create-outline',
    defaultWidth: 220,
    defaultHeight: 40,
    editableText: true,
    html: `<div style="font-size:13px;color:#111827;">Double-tap to edit this text</div>`,
  },
  {
    type: 'divider',
    label: 'Divider',
    icon: 'remove-outline',
    defaultWidth: 560,
    defaultHeight: 2,
    html: `<div style="width:100%;height:100%;background:{{colors.primary}};"></div>`,
  },
  {
    type: 'shape',
    label: 'Rectangle',
    icon: 'square-outline',
    defaultWidth: 160,
    defaultHeight: 100,
    html: `<div style="width:100%;height:100%;background:{{colors.primary}};opacity:0.12;border:1px solid {{colors.primary}};border-radius:6px;"></div>`,
  },
];

/** {{placeholder}} chips offered while editing a text block — must match
 * QuotationPdfService.buildContext's actual context shape. */
export const QUOTATION_PLACEHOLDERS: PlaceholderGroup[] = [
  {
    label: 'Document',
    items: [
      { label: 'Document Number', value: '{{documentNumber}}' },
      { label: 'Document Title', value: '{{documentTitle}}' },
      { label: 'Date', value: '{{date}}' },
      { label: 'Valid Until', value: '{{validUntil}}' },
    ],
  },
  {
    label: 'Customer',
    items: [
      { label: 'Name', value: '{{customerName}}' },
      { label: 'Phone', value: '{{customerPhone}}' },
      { label: 'Email', value: '{{customerEmail}}' },
      { label: 'Address', value: '{{customerAddress}}' },
    ],
  },
  {
    label: 'Business',
    items: [
      { label: 'Name', value: '{{business.name}}' },
      { label: 'Address', value: '{{business.address}}' },
      { label: 'Phone', value: '{{business.phone}}' },
      { label: 'Email', value: '{{business.email}}' },
    ],
  },
  {
    label: 'Totals',
    items: [
      { label: 'Currency', value: '{{currency}}' },
      { label: 'Subtotal', value: '{{subtotal}}' },
      { label: 'Tax', value: '{{tax}}' },
      { label: 'Shipping', value: '{{shipping}}' },
      { label: 'Discount', value: '{{discount}}' },
      { label: 'Total', value: '{{totalPrice}}' },
      { label: 'Notes', value: '{{notes}}' },
    ],
  },
];
